/**
 * Single source of truth for development/localhost detection and dev-mode header styling.
 * Change border, badge, or center label design here to update all headers across the app.
 */

/** True when running on localhost or 127.0.0.1 (safe for SSR). */
export function isDevMode() {
  return typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
}

const INTERNAL_WARNING_EMAILS = new Set([
  'rhys100@gmail.com',
  'rhys@phaserfpv.com.au',
  'dev@localhost',
]);

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

/** Picking/ops diagnostics — hidden from warehouse pickers on production. */
export function canViewInternalWarnings(currentUser) {
  if (isDevMode()) return true;
  return INTERNAL_WARNING_EMAILS.has(normalizeEmail(currentUser?.email));
}

/** Styles to apply to header bars in dev mode (outline avoids layout shift vs border). */
export const devModeHeaderStyles = {
  outline: '3px dotted #e53e3e',
  outlineOffset: '-3px',
  boxShadow: 'inset 0 0 0 1px rgba(229, 62, 62, 0.3)'
};

/** Returns devModeHeaderStyles when isDevMode(), else {}. Use: style={{ ...base, ...getDevModeHeaderStyles() }} */
export function getDevModeHeaderStyles() {
  return isDevMode() ? devModeHeaderStyles : {};
}

/** Inline styles for the small "⚠️ DEV" badge shown on module headers. */
export const devModeBadgeStyle = {
  fontSize: '12px',
  fontWeight: 700,
  color: '#fbbf24',
  letterSpacing: '0.05em'
};

/** Inline styles for the main header center label "⚠️ DEV MODE — localhost". */
export const devModeCenterLabelStyle = {
  fontSize: '13px',
  fontWeight: 700,
  color: '#fbbf24',
  textShadow: '0 0 8px rgba(251, 191, 36, 0.5)',
  letterSpacing: '0.1em',
  whiteSpace: 'nowrap'
};

/** Wrapper style for the center label (grid column 2 of module-header). */
export const devModeCenterLabelWrapperStyle = {
  pointerEvents: 'none',
  justifySelf: 'center',
  gridColumn: '2'
};

/** Small "⚠️ DEV" badge for module headers. Renders nothing when not in dev mode. */
export function DevModeBadge() {
  if (!isDevMode()) return null;
  return <span style={devModeBadgeStyle}>⚠️ DEV</span>;
}

/** Center label for main warehouse header: "⚠️ DEV MODE — localhost". Renders nothing when not in dev mode. */
export function DevModeCenterLabel() {
  if (!isDevMode()) return null;
  return (
    <div className="module-header-dev-center" style={devModeCenterLabelWrapperStyle}>
      <span style={devModeCenterLabelStyle}>⚠️ DEV MODE — localhost</span>
    </div>
  );
}

/**
 * Slim "DEV localhost" pill for the main header. It sits in its own grid column
 * at the far left and takes real layout space, so it can never be drawn over
 * module buttons (the old centre label overlapped them when every module was on).
 */
export function DevModePill() {
  if (!isDevMode()) return null;
  return (
    <span className="module-header-dev-pill" title="Development mode — localhost">
      ⚠️ DEV<span className="module-header-dev-pill__host"> · localhost</span>
    </span>
  );
}
